import { TypedDocumentNode, useQuery } from '@apollo/client'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { debounce } from 'lodash'
import { Autocomplete, autocompleteClasses, AutocompleteInputChangeReason, AutocompleteRenderOptionState, Box, Stack } from '@mui/material'
import { TextField } from '@vapor/material'
import SearchIcon from '@mui/icons-material/Search'

export type QueryLookupProps<T extends { id: string } | { _id: string }, QueryData extends object, QueryVariables extends object> = {
  value: T | { id: string } | null
  onChange: (value: T | null) => void
  queryDocument: TypedDocumentNode<QueryData, QueryVariables>
  fetchQueryVariables: (id: string) => QueryVariables
  getFetchRecord: (data: QueryData) => T | null
  searchQueryVariables: (text: string) => QueryVariables
  getSearchRecords: (data: QueryData) => T[]
  getOptionLabel: (option: T) => string
  minSearchParameterLenght?: number
  disabled?: boolean
  error?: boolean
  renderOption?: (option: T) => React.ReactNode
  footerOption?: (inputValue: string, onClose: () => void) => React.ReactNode
  clearOnBlur?: boolean
  onBlur?: (inputValue: string) => void
  size?: 'small' | 'medium'
  noOptionsText?: React.ReactNode
}

type RenderOption<T> = (props: React.HTMLAttributes<HTMLLIElement>, option: T, state: AutocompleteRenderOptionState) => React.ReactNode

export default function QueryLookup<T extends { id: string } | { _id: string }, QueryData extends object, QueryVariables extends object>(props: QueryLookupProps<T, QueryData, QueryVariables>) {
  //#region PROPS
  const { value, onChange, queryDocument, fetchQueryVariables, getFetchRecord, searchQueryVariables, getSearchRecords, getOptionLabel, disabled, error, footerOption, clearOnBlur, onBlur } = props
  const minSearchParameterLenght = props.minSearchParameterLenght || 3
  const renderOption: RenderOption<T> | undefined = useMemo(() => {
    if (props.renderOption) {
      return (p: React.HTMLAttributes<HTMLLIElement>, option: T, state: AutocompleteRenderOptionState) => (
        <Box component="li" {...p} key={'id' in option ? option.id : option._id}>
          {props.renderOption?.(option)}
        </Box>
      )
    } else {
      return undefined
    }
  }, [props.renderOption])
  //#endregion PROPS

  //#region LOCAL STATE
  const [options, setOptions] = useState<T[]>([])
  const [localValue, setLocalValue] = useState<T | null>(null)
  const onLocalValueChange = useCallback(
    (newLocalValue: T | null) => {
      if (JSON.stringify(newLocalValue) !== JSON.stringify(localValue)) {
        setLocalValue(newLocalValue)
      }
      if (JSON.stringify(newLocalValue) !== JSON.stringify(value)) {
        onChange(newLocalValue)
      }
    },
    [localValue, value, onChange],
  )
  const [inputValue, setInputValue] = useState('')
  const [debouncedInputValue, setDebouncedInputValue] = useState('')
  const [open, setOpen] = useState(false)
  const PaperComponent: React.JSXElementConstructor<React.HTMLAttributes<HTMLElement>> | undefined = useMemo(() => {
    if (footerOption) {
      return (params: React.HTMLAttributes<HTMLElement>) => (
        <Stack {...params}>
          <Stack direction="column" spacing={1}>
            {params.children}
            {footerOption(inputValue, () => setOpen(false))}
          </Stack>
        </Stack>
      )
    }
    return undefined
  }, [footerOption, inputValue])
  //#endregion LOCAL STATE

  //#region CONTROLLED STATE
  useEffect(() => {
    if (isFragment(value)) {
      setOptions([value])
      onLocalValueChange(value)
    } else if (value === null) {
      setOptions([])
      onLocalValueChange(null)
    }
  }, [value])
  //#endregion CONTROLLED STATE

  //#region FETCH
  const fetchSkip = useMemo(() => {
    return value === null || ('id' in value ? value.id : value._id) === (localValue && 'id' in localValue ? localValue?.id : localValue?._id) || ('id' in value && value.id === '') || isFragment(value)
  }, [value, localValue])
  const fetchVariables = useMemo(() => {
    const id = value ? ('id' in value ? value.id : value._id) : undefined
    return id ? fetchQueryVariables(id) : undefined
  }, [value])

  const { data: fetchData } = useQuery(queryDocument, {
    skip: fetchSkip,
    variables: fetchVariables,
  })

  useEffect(() => {
    if (fetchData) {
      const fetched = getFetchRecord(fetchData)
      if (localValue === null || JSON.stringify(localValue) !== JSON.stringify(fetched)) {
        setOptions(fetched ? [fetched] : [])
        onLocalValueChange(fetched)
      }
    }
  }, [fetchData])
  //#endregion FETCH

  //#region SEARCH
  const debonceSetInputValue = useMemo(() => debounce((text: string) => setDebouncedInputValue(text), 300), [])
  const onInputChange = useCallback(
    (event: React.SyntheticEvent, value: string, reason: AutocompleteInputChangeReason) => {
      setInputValue(value)
      if (value.length < minSearchParameterLenght) {
        setOptions([])
        debonceSetInputValue('')
      } else if (reason === 'input') {
        debonceSetInputValue(value)
      } else {
        debonceSetInputValue('')
        if (localValue) {
          setOptions([localValue])
        } else {
          setOptions([])
        }
      }
    },
    [minSearchParameterLenght],
  )
  const searchSkip = useMemo(() => debouncedInputValue.length < minSearchParameterLenght, [debouncedInputValue])
  const searchVariables = useMemo(() => searchQueryVariables(debouncedInputValue), [debouncedInputValue])
  const { data: searchData } = useQuery(queryDocument, { skip: searchSkip, variables: searchVariables })
  useEffect(() => {
    if (searchData) {
      setOptions(getSearchRecords(searchData))
    }
  }, [searchData])
  //#endregion SEARCH

  return (
    <Autocomplete //
      size={props.size}
      fullWidth
      value={localValue}
      onChange={(event, value) => onLocalValueChange(value)}
      open={open}
      onClose={() => setOpen(false)}
      onOpen={() => setOpen(true)}
      options={!localValue || options.some((o) => ('id' in o ? o.id : o._id) === (localValue && 'id' in localValue ? localValue.id : localValue._id)) ? options : [...options, localValue]}
      filterOptions={(x) => x}
      onInputChange={onInputChange}
      renderInput={(params) => {
        return <TextField {...params} size={props.size} error={error} />
      }}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(option, value) => (option && 'id' in option ? option.id : option._id) === (value && 'id' in value ? value.id : value._id)}
      disabled={disabled}
      renderOption={renderOption}
      PaperComponent={PaperComponent}
      componentsProps={{ paper: { sx: { width: 'fit-content', minWidth: '100%', background: 'white', boxShadow: 5 } } }}
      popupIcon={<SearchIcon />}
      clearOnBlur={clearOnBlur}
      onBlur={() => {
        if (onBlur && (localValue && 'id' in localValue ? !localValue?.id : !localValue?._id)) onBlur(inputValue)
      }}
      noOptionsText={props.noOptionsText}
      sx={{
        [`& .${autocompleteClasses.popupIndicator}`]: {
          transform: 'none',
        },
      }}
    />
  )
}

export function isId<T extends { id: string }>(value: T | { id: string } | { _id: string } | null): value is { id: string } {
  if (value === null || value === undefined) {
    return false
  }
  const keys = Object.keys(value)
  return keys.length === 1 && keys[0] === 'id'
}

export function isFragment<T extends { id: string } | { _id: string }>(value: T | { id: string } | { _id: string } | null): value is T {
  if (value === null || value === undefined) {
    return false
  }
  return !isId(value)
}
